import React, { Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { Environment, Grid, useProgress } from '@react-three/drei';
import ViewerCamera from './ViewerCamera';
import ViewerControls from './ViewerControls';
import ViewerLights from './ViewerLights';
import GLTFModel from './GLTFModel';
import { DEFAULTS } from './defaults';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Merge user-supplied props with DEFAULTS.
 * `undefined` values are ignored so prop-less usage (`<ThreeDmodalRender />`) always
 * gets the correct defaults.  Explicitly passing `null` DOES override a default,
 * enabling things like `environment={null}` to clear an ambient preset.
 */
function mergeWithDefaults(defaults, userProps) {
  const merged = { ...defaults };
  for (const [key, value] of Object.entries(userProps)) {
    if (value !== undefined) merged[key] = value;
  }
  return merged;
}

// ---------------------------------------------------------------------------
// Error boundary — catches GLTF load failures inside the Canvas
// ---------------------------------------------------------------------------

class ModelErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    // Surface the real failure (bad URL, CORS, unsupported compression, etc.)
    // instead of leaving only a blank grey canvas behind.
    console.error('[ThreeDmodalRender] failed to load model:', error);
    this.props.onError?.(error);
  }

  render() {
    // On error, render nothing inside the Canvas — the scene simply stays empty.
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// Loading overlay (HTML — rendered above the Canvas)
// ---------------------------------------------------------------------------

function DefaultLoadingOverlay() {
  // Match the project-wide spinner (see <Loader /> in src/Components/Loader),
  // tinted with the client's primary color.
  const primary = window.__E_TT_Client?.primary || '#ff943d';
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10,
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      <div
        className="loadingSpinner"
        style={{ borderColor: `${primary} transparent ${primary} transparent` }}
      ></div>
    </div>
  );
}

function ErrorOverlay() {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '0 24px',
        color: '#555',
        fontSize: '13px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        zIndex: 10,
        userSelect: 'none',
      }}
    >
      Unable to load 3D model.
    </div>
  );
}

/**
 * Subscribes to THREE.DefaultLoadingManager progress via `useProgress`
 * (safe to call outside the Canvas).  Renders the custom loadingComponent
 * when provided, or the built-in overlay while assets are in-flight.
 */
function LoadingOverlay({ loadingComponent }) {
  const { active } = useProgress();
  if (!active) return null;
  return loadingComponent ?? <DefaultLoadingOverlay />;
}

// ---------------------------------------------------------------------------
// Scene content — everything rendered inside the Canvas
// ---------------------------------------------------------------------------

function SceneContent({ config }) {
  return (
    <>
      {/* Background clear color synced with the wrapper div */}
      <color attach="background" args={[config.backgroundColor]} />

      <ViewerCamera
        cameraType={config.cameraType}
        cameraPosition={config.cameraPosition}
        fov={config.fov}
      />

      <ViewerControls
        autoRotate={config.autoRotate}
        autoRotateSpeed={config.autoRotateSpeed}
        enableZoom={config.enableZoom}
        enablePan={config.enablePan}
        enableRotate={config.enableRotate}
        minDistance={config.minDistance}
        maxDistance={config.maxDistance}
        minPolarAngle={config.minPolarAngle}
        maxPolarAngle={config.maxPolarAngle}
      />

      {/* Environment preset takes priority over manual lights */}
      {config.environment ? (
        <Environment preset={config.environment} />
      ) : (
        <ViewerLights
          ambientIntensity={config.ambientIntensity}
          directionalIntensity={config.directionalIntensity}
          pointIntensity={config.pointIntensity}
          shadows={config.shadows}
        />
      )}

      {config.showGrid && (
        <Grid
          args={[20, 20]}
          cellSize={0.5}
          cellThickness={0.5}
          cellColor="#6f6f6f"
          sectionSize={3}
          sectionThickness={1}
          sectionColor="#9d9d9d"
          fadeDistance={25}
          fadeStrength={1}
        />
      )}

      {config.showAxes && <axesHelper args={[5]} />}

      {/* Error boundary isolates model-load failures from the rest of the scene */}
      <ModelErrorBoundary onError={config.onError}>
        <Suspense fallback={null}>
          <GLTFModel
            url={config.modelUrl}
            centerModel={config.centerModel}
            fitToView={config.fitToView}
            scale={config.scale}
            wireframe={config.wireframe}
            shadows={config.shadows}
            onModelLoad={config.onModelLoad}
          />
        </Suspense>
      </ModelErrorBoundary>
    </>
  );
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * ThreeDmodalRender — zero-config drop-in 3D model viewer.
 *
 * Minimal usage (loads /hamburger.glb with sensible defaults):
 *   <ThreeDmodalRender />
 *
 * Full usage:
 *   <ThreeDmodalRender
 *     modelUrl="/car.glb"
 *     cameraType="perspective"
 *     cameraPosition={[4, 2, 6]}
 *     fov={50}
 *     autoRotate={false}
 *     autoRotateSpeed={1}
 *     enableZoom
 *     enablePan={false}
 *     minDistance={2}
 *     maxDistance={30}
 *     minPolarAngle={Math.PI / 6}
 *     maxPolarAngle={Math.PI / 2}
 *     backgroundColor="#1a1a2e"
 *     shadows
 *     ambientIntensity={0.5}
 *     directionalIntensity={2}
 *     pointIntensity={1.5}
 *     fitToView
 *     centerModel
 *     scale={1.2}
 *     showGrid
 *     showAxes
 *     wireframe={false}
 *     environment="studio"
 *     loadingComponent={<MySpinner />}
 *     onModelLoad={(scene) => console.log('loaded', scene)}
 *     onError={(err) => console.error(err)}
 *   />
 *
 * All props are optional.  See src/components/defaults.js for default values.
 */
function ThreeDmodalRender(userProps) {
  const config = mergeWithDefaults(DEFAULTS, userProps);
  const [loadError, setLoadError] = React.useState(null);

  const handleError = React.useCallback(
    (error) => {
      setLoadError(error);
      config.onError?.(error);
    },
    [config]
  );

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        background: "transparent",
        overflow: 'hidden',
      }}
    >
      <Canvas
        shadows={config.shadows}
        gl={{ antialias: true, alpha: false }}
        dpr={[1, 2]}
        resize={{ scroll: false, offsetSize: true, debounce: { scroll: 0, resize: 0 } }}
        style={{ width: '100%', height: '100%', display: 'block' }}
      >
        <SceneContent config={{ ...config, onError: handleError }} />
      </Canvas>

      {/* HTML overlay sits above the WebGL canvas */}
      {loadError ? <ErrorOverlay /> : <LoadingOverlay loadingComponent={config.loadingComponent} />}
    </div>
  );
}

export default ThreeDmodalRender;
